import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Search, ShoppingCart, User, X, Plus, Minus,
  MapPin, Phone, Mail, Eye, EyeOff, ChevronRight,
  Package, Heart, LogOut, ChevronLeft, ArrowRight,
  Truck, Shield, Clock, Star, Box, Menu
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportBaba = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [authTab, setAuthTab] = useState("login");
  const [currentPage, setCurrentPage] = useState("home");
  const [accountTab, setAccountTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [productModal, setProductModal] = useState(null);
  const [productVariants, setProductVariants] = useState([]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [showMiniCart, setShowMiniCart] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    firstName: "", lastName: "", email: "", password: "",
    phone: "", businessName: "", businessAddress: "",
    city: "", state: "", country: "United States", zipCode: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [heroFade, setHeroFade] = useState(true);
  const productScrollRef = useRef(null);

  useEffect(() => {
    const imgs = storeSettings?.banner_images || [];
    if (imgs.length === 0) return;
    const interval = setInterval(() => {
      setHeroFade(false);
      setTimeout(() => {
        setBannerIndex(prev => (prev + 1) % imgs.length);
        setHeroFade(true);
      }, 400);
    }, 5000);
    return () => clearInterval(interval);
  }, [storeSettings]);

  useEffect(() => {
    loadProducts(); loadCategories(); loadStoreSettings();
    const savedToken = localStorage.getItem("importbaba_token");
    const savedUser = localStorage.getItem("importbaba_user");
    const savedCart = localStorage.getItem("importbaba_cart");
    const savedWishlist = localStorage.getItem("importbaba_wishlist");
    if (savedToken && savedUser) { setToken(savedToken); setUser(JSON.parse(savedUser)); setIsLoggedIn(true); }
    if (savedCart) setCart(JSON.parse(savedCart));
    if (savedWishlist) setWishlist(JSON.parse(savedWishlist));
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      axios.get(`${API}/storefront/orders?limit=50`).then(res => {
        setOrders((res.data.orders || []).map(o => ({ ...o, id: o.order_id, date: o.created_at })));
      }).catch(() => {});
    }
  }, [isLoggedIn, token]);

  useEffect(() => { localStorage.setItem("importbaba_cart", JSON.stringify(cart)); }, [cart]);

  const loadProducts = async () => { try { const res = await axios.get(`${API}/supplier-products?limit=500`); setProducts(res.data.products || []); } catch {} };
  const loadCategories = async () => { try { const res = await axios.get(`${API}/supplier-products/stats`); if (res.data.by_category) setCategories(Object.entries(res.data.by_category).map(([name, count]) => ({ name, count }))); } catch {} };
  const loadStoreSettings = async () => { try { const res = await axios.get(`${API}/store-settings`); setStoreSettings(res.data); } catch {} };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) { toast.error("Please enter email and password"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/storefront/auth/login`, { email: loginForm.email, password: loginForm.password });
      const { token: t, user: u } = res.data;
      setToken(t); setUser(u); setIsLoggedIn(true);
      localStorage.setItem("importbaba_token", t); localStorage.setItem("importbaba_user", JSON.stringify(u));
      toast.success("Welcome back!"); setCurrentPage("shop");
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid credentials"); }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerForm.email || !registerForm.password || !registerForm.firstName || !registerForm.lastName) { toast.error("Please fill required fields"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/storefront/auth/register`, {
        first_name: registerForm.firstName, last_name: registerForm.lastName, email: registerForm.email,
        password: registerForm.password, phone: registerForm.phone, business_name: registerForm.businessName,
        business_address: registerForm.businessAddress, city: registerForm.city, state: registerForm.state,
        country: registerForm.country, zip_code: registerForm.zipCode,
      });
      const { token: t, user: u } = res.data;
      setToken(t); setUser(u); setIsLoggedIn(true);
      localStorage.setItem("importbaba_token", t); localStorage.setItem("importbaba_user", JSON.stringify(u));
      toast.success("Account created!"); setCurrentPage("shop");
    } catch (err) { toast.error(err.response?.data?.detail || "Registration failed"); }
    setLoading(false);
  };

  const handleLogout = () => {
    setUser(null); setToken(null); setIsLoggedIn(false);
    localStorage.removeItem("importbaba_user"); localStorage.removeItem("importbaba_token");
    setCurrentPage("home"); toast.success("Logged out");
  };

  const addToCart = (product, quantity = 1) => {
    setCart(prev => {
      const existing = prev.find(i => i.sku === product.sku);
      if (existing) return prev.map(i => i.sku === product.sku ? { ...i, quantity: i.quantity + quantity } : i);
      return [...prev, { ...product, quantity }];
    });
    toast.success("Added to cart");
  };

  const updateCartQuantity = (sku, q) => { if (q < 1) { removeFromCart(sku); return; } setCart(prev => prev.map(i => i.sku === sku ? { ...i, quantity: q } : i)); };
  const removeFromCart = (sku) => setCart(prev => prev.filter(i => i.sku !== sku));
  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.quantity, 0), [cart]);

  const toggleWishlist = (product) => {
    const exists = wishlist.find(p => p.sku === product.sku);
    const updated = exists ? wishlist.filter(p => p.sku !== product.sku) : [...wishlist, product];
    setWishlist(updated); localStorage.setItem("importbaba_wishlist", JSON.stringify(updated));
    toast.success(exists ? "Removed from wishlist" : "Added to wishlist");
  };

  const openProductModal = (product) => { setProductModal(product); setProductVariants(product.variants || []); };

  const filteredProducts = useMemo(() => products.filter(p => {
    const matchSearch = !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku?.toLowerCase().includes(searchQuery.toLowerCase()) || p.variants?.some(v => v.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchCat = selectedCategory === "all" || p.category?.toUpperCase() === selectedCategory.toUpperCase();
    return matchSearch && matchCat;
  }), [products, searchQuery, selectedCategory]);

  const s = storeSettings || {};
  const bannerImages = s.banner_images || [];

  const scrollProducts = useCallback((dir) => {
    if (!productScrollRef.current) return;
    const amount = dir === "left" ? -400 : 400;
    productScrollRef.current.scrollBy({ left: amount, behavior: "smooth" });
  }, []);

  // ============================================
  // HEADER - Dark glass
  // ============================================
  const Header = () => (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="backdrop-blur-xl bg-black/70 border-b border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setCurrentPage(isLoggedIn ? "shop" : "home")} data-testid="logo-btn">
              <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
                <span className="text-black font-extrabold text-sm tracking-tight" style={{ fontFamily: 'Manrope' }}>IB</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-white tracking-tight leading-none" style={{ fontFamily: 'Manrope' }}>ImportBaba</h1>
                <p className="text-[9px] text-white/40 tracking-[0.25em] uppercase">Wholesale Hub</p>
              </div>
            </div>

            {/* Center nav links - desktop */}
            <nav className="hidden lg:flex items-center gap-8">
              <button onClick={() => setCurrentPage(isLoggedIn ? "shop" : "home")}
                className="text-sm text-white/70 hover:text-white transition-colors font-medium tracking-wide">Home</button>
              <button onClick={() => { setSelectedCategory("all"); setCurrentPage("shop"); }}
                className="text-sm text-white/70 hover:text-white transition-colors font-medium tracking-wide">Products</button>
              {categories.slice(0, 3).map(cat => (
                <button key={cat.name} onClick={() => { setSelectedCategory(cat.name); setCurrentPage("shop"); }}
                  className="text-sm text-white/70 hover:text-white transition-colors font-medium tracking-wide">{cat.name}</button>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="hidden md:flex">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={16} />
                  <input type="text" placeholder="Search..." value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); if (currentPage !== "shop") setCurrentPage("shop"); }}
                    className="w-48 pl-9 pr-4 py-1.5 bg-white/5 border border-white/10 rounded-full text-sm text-white placeholder:text-white/30 focus:outline-none focus:bg-white/10 focus:border-white/20 transition-all focus:w-64"
                    data-testid="search-input" />
                </div>
              </div>

              <button onClick={() => { setCurrentPage("account"); setAccountTab("wishlist"); }}
                className="relative p-2 text-white/50 hover:text-white transition-colors" data-testid="wishlist-btn">
                <Heart size={20} />
                {wishlist.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-white text-black text-[10px] font-bold rounded-full flex items-center justify-center">{wishlist.length}</span>}
              </button>

              {isLoggedIn ? (
                <button onClick={() => setCurrentPage("account")} className="hidden md:flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="account-btn">
                  <div className="w-7 h-7 bg-white/10 text-white rounded-full flex items-center justify-center font-semibold text-xs border border-white/10">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                </button>
              ) : (
                <button onClick={() => setCurrentPage("home")} className="hidden md:flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white/80 hover:text-white border border-white/15 rounded-full hover:bg-white/5 transition-all" data-testid="signin-btn">
                  <User size={14} />Sign In
                </button>
              )}

              <div className="relative">
                <button onClick={() => setShowMiniCart(!showMiniCart)}
                  className="flex items-center gap-2 px-4 py-1.5 bg-white text-black rounded-full hover:bg-white/90 transition-all active:scale-95 text-sm font-semibold" data-testid="cart-btn">
                  <ShoppingCart size={16} />
                  <span className="hidden md:inline">${cartTotal.toFixed(2)}</span>
                  {cartCount > 0 && <span className="w-5 h-5 bg-black text-white text-[10px] font-bold rounded-full flex items-center justify-center">{cartCount}</span>}
                </button>

                {showMiniCart && (
                  <div className="absolute right-0 top-full mt-3 w-96 bg-zinc-900 rounded-2xl shadow-2xl border border-white/10 z-50 backdrop-blur-xl" data-testid="mini-cart">
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-lg text-white" style={{ fontFamily: 'Manrope' }}>Cart ({cartCount})</h3>
                        <button onClick={() => setShowMiniCart(false)} className="text-white/40 hover:text-white"><X size={18} /></button>
                      </div>
                      {cart.length > 0 ? (
                        <>
                          <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
                            {cart.map(item => (
                              <div key={item.sku} className="flex gap-3 p-2 rounded-lg hover:bg-white/5">
                                <div className="w-14 h-14 bg-white/5 rounded-lg flex-shrink-0 overflow-hidden">
                                  {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-contain" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-white truncate">{item.name}</p>
                                  <p className="text-xs text-white/40 mt-0.5">{item.quantity} x ${item.price?.toFixed(2)}</p>
                                </div>
                                <button onClick={() => removeFromCart(item.sku)} className="text-white/20 hover:text-red-400 transition-colors"><X size={16} /></button>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="flex justify-between font-bold text-lg mb-4 text-white"><span>Total</span><span>${cartTotal.toFixed(2)}</span></div>
                            <button onClick={() => { setCurrentPage("cart"); setShowMiniCart(false); }}
                              className="w-full py-3 bg-white text-black rounded-full font-semibold hover:bg-white/90 transition-all active:scale-[0.98]">
                              View Cart & Checkout
                            </button>
                          </div>
                        </>
                      ) : <p className="text-white/40 text-center py-8">Your cart is empty</p>}
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile menu */}
              <button onClick={() => setMobileMenu(!mobileMenu)} className="lg:hidden p-2 text-white/60 hover:text-white">
                <Menu size={22} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {mobileMenu && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/5 px-6 py-4 space-y-3">
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={16} />
            <input type="text" placeholder="Search products..." value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); if (currentPage !== "shop") setCurrentPage("shop"); setMobileMenu(false); }}
              className="w-full pl-9 pr-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-white placeholder:text-white/30 focus:outline-none" />
          </div>
          <button onClick={() => { setCurrentPage("home"); setMobileMenu(false); }} className="block w-full text-left text-white/70 hover:text-white py-2 text-sm">Home</button>
          <button onClick={() => { setSelectedCategory("all"); setCurrentPage("shop"); setMobileMenu(false); }} className="block w-full text-left text-white/70 hover:text-white py-2 text-sm">All Products</button>
          {!isLoggedIn && <button onClick={() => { setCurrentPage("home"); setMobileMenu(false); }} className="block w-full text-left text-white/70 hover:text-white py-2 text-sm">Sign In / Register</button>}
          {isLoggedIn && <button onClick={() => { setCurrentPage("account"); setMobileMenu(false); }} className="block w-full text-left text-white/70 hover:text-white py-2 text-sm">My Account</button>}
        </div>
      )}
    </header>
  );

  // ============================================
  // FULL-SCREEN HERO SLIDER (GeekBar style)
  // ============================================
  const HeroBanner = () => (
    <div className="relative w-full h-screen bg-black overflow-hidden" data-testid="hero-banner">
      {bannerImages.length > 0 ? (
        <>
          <img
            src={bannerImages[bannerIndex % bannerImages.length]}
            alt={`Slide ${bannerIndex + 1}`}
            className="w-full h-full object-cover transition-opacity duration-500"
            style={{ opacity: heroFade ? 1 : 0 }}
            onError={e => { e.target.style.opacity = '0'; }}
          />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

          {/* Nav arrows */}
          <button onClick={() => setBannerIndex(prev => (prev - 1 + bannerImages.length) % bannerImages.length)}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/5 hover:bg-white/15 text-white rounded-full flex items-center justify-center backdrop-blur-md transition-all border border-white/10"
            data-testid="hero-prev-btn">
            <ChevronLeft size={24} />
          </button>
          <button onClick={() => setBannerIndex(prev => (prev + 1) % bannerImages.length)}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/5 hover:bg-white/15 text-white rounded-full flex items-center justify-center backdrop-blur-md transition-all border border-white/10"
            data-testid="hero-next-btn">
            <ChevronRight size={24} />
          </button>

          {/* Slide indicators */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
            {bannerImages.map((_, i) => (
              <button key={i} onClick={() => setBannerIndex(i)}
                className={`rounded-full transition-all duration-300 ${i === bannerIndex % bannerImages.length ? "bg-white w-8 h-2" : "bg-white/30 w-2 h-2 hover:bg-white/50"}`}
                data-testid={`hero-dot-${i}`} />
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="absolute bottom-20 left-0 right-0 text-center">
            <button onClick={() => setCurrentPage("shop")}
              className="px-8 py-3 bg-white text-black rounded-full font-bold text-sm hover:bg-white/90 transition-all hover:scale-105 active:scale-95 tracking-wide" data-testid="explore-products-btn">
              EXPLORE PRODUCTS
              <ArrowRight size={16} className="inline ml-2" />
            </button>
          </div>
        </>
      ) : (
        <div className="w-full h-full bg-black flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>ImportBaba</h1>
            <p className="text-white/40 mt-4 text-lg tracking-[0.2em] uppercase">Premium Wholesale Products</p>
          </div>
        </div>
      )}
    </div>
  );

  // ============================================
  // SCROLLING MARQUEE (GeekBar style)
  // ============================================
  const Marquee = () => (
    <div className="bg-black py-3 overflow-hidden border-y border-white/5">
      <div className="flex animate-marquee whitespace-nowrap">
        {Array(8).fill(null).map((_, i) => (
          <span key={i} className="mx-8 text-sm text-white/30 font-medium tracking-widest uppercase">
            ImportBaba Wholesale
            <span className="mx-4 text-white/10">|</span>
            Premium Products
            <span className="mx-4 text-white/10">|</span>
            Best Wholesale Prices
            <span className="mx-4 text-white/10">|</span>
            Fast Shipping
          </span>
        ))}
      </div>
    </div>
  );

  // ============================================
  // PRODUCT SHOWCASE - Horizontal scroll (GeekBar style)
  // ============================================
  const ProductShowcase = () => {
    const featured = products.slice(0, 10);
    if (featured.length === 0) return null;

    return (
      <div className="bg-black py-16 relative" data-testid="product-showcase">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-white/30 text-xs tracking-[0.3em] uppercase mb-2">Featured</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>Products</h2>
            </div>
            <div className="flex gap-2">
              <button onClick={() => scrollProducts("left")}
                className="w-10 h-10 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white rounded-full flex items-center justify-center border border-white/10 transition-all">
                <ChevronLeft size={18} />
              </button>
              <button onClick={() => scrollProducts("right")}
                className="w-10 h-10 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white rounded-full flex items-center justify-center border border-white/10 transition-all">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          <div ref={productScrollRef} className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
            {featured.map(product => (
              <div key={product.sku}
                onClick={() => openProductModal(product)}
                className="group flex-shrink-0 w-[300px] md:w-[340px] bg-zinc-900/50 rounded-2xl border border-white/5 hover:border-white/15 transition-all duration-500 cursor-pointer snap-start overflow-hidden"
                data-testid="showcase-product-card">
                <div className="relative aspect-[4/3] bg-gradient-to-b from-zinc-800/50 to-zinc-900 p-6 overflow-hidden">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/10"><Package size={64} /></div>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); toggleWishlist(product); }}
                    className={`absolute top-4 right-4 p-2 rounded-full transition-all ${wishlist.find(p => p.sku === product.sku) ? "bg-red-500/20 text-red-400" : "bg-black/30 text-white/40 hover:text-white opacity-0 group-hover:opacity-100"}`}>
                    <Heart size={16} fill={wishlist.find(p => p.sku === product.sku) ? "currentColor" : "none"} />
                  </button>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-white text-base mb-2 line-clamp-1 group-hover:text-white/90" style={{ fontFamily: 'Manrope' }}>
                    {product.name}
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {product.variants?.length > 1 && (
                      <span className="px-2.5 py-1 bg-white/5 text-white/50 text-xs rounded-full border border-white/5">
                        {product.variants.length} Variants
                      </span>
                    )}
                    {product.stock_quantity > 0 && (
                      <span className="px-2.5 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded-full border border-emerald-500/10">
                        In Stock
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                      ${product.price?.toFixed(2) || "0.00"}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); product.variants?.length > 1 ? openProductModal(product) : addToCart(product); }}
                      className="px-4 py-2 bg-white text-black rounded-full text-xs font-bold hover:bg-white/90 transition-all active:scale-95"
                      data-testid="showcase-add-btn">
                      {product.variants?.length > 1 ? "Select" : "Add"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // TRUST SECTION - Dark style
  // ============================================
  const TrustSection = () => (
    <div className="bg-zinc-950 border-y border-white/5" data-testid="trust-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Truck, label: "Fast Shipping", desc: "2-5 business days" },
            { icon: Shield, label: "Verified Products", desc: "100% authentic" },
            { icon: Clock, label: "Business Hours", desc: s.business_hours?.mon_fri || "10am-7pm CST" },
            { icon: Star, label: "Wholesale Prices", desc: "Best rates guaranteed" },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="flex items-center gap-4 p-4">
              <div className="w-12 h-12 bg-white/5 text-white/60 rounded-xl flex items-center justify-center flex-shrink-0 border border-white/5">
                <Icon size={22} />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{label}</p>
                <p className="text-xs text-white/40">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ============================================
  // PRODUCT CARD - Dark style
  // ============================================
  const ProductCard = ({ product }) => {
    const isWish = wishlist.find(p => p.sku === product.sku);
    const variantCount = product.variants?.length || 0;

    return (
      <div className="group relative bg-zinc-900/60 rounded-2xl border border-white/5 hover:border-white/15 transition-all duration-300" data-testid="product-card">
        <div className="relative cursor-pointer overflow-hidden rounded-t-2xl" onClick={() => openProductModal(product)}>
          <div className="aspect-square bg-gradient-to-b from-zinc-800/30 to-zinc-900/50 p-6 group-hover:from-zinc-700/30 transition-colors duration-500">
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/10"><Package size={56} /></div>
            )}
          </div>
          <button onClick={(e) => { e.stopPropagation(); toggleWishlist(product); }}
            className={`absolute top-3 right-3 p-2 rounded-full transition-all ${isWish ? "bg-red-500/20 text-red-400" : "bg-black/40 backdrop-blur-sm text-white/30 hover:text-white opacity-0 group-hover:opacity-100"}`}>
            <Heart size={16} fill={isWish ? "currentColor" : "none"} />
          </button>
          {variantCount > 1 && (
            <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur-sm text-white/80 text-xs font-medium rounded-full border border-white/10">
              {variantCount} flavors
            </div>
          )}
        </div>

        <div className="p-4">
          <p className="text-[10px] font-medium text-white/30 uppercase tracking-widest mb-1.5">{product.category}</p>
          <h3 className="font-semibold text-white line-clamp-2 min-h-[40px] text-sm leading-snug mb-2 cursor-pointer hover:text-white/80 transition-colors"
            onClick={() => openProductModal(product)} style={{ fontFamily: 'Manrope' }}>
            {product.name}
          </h3>
          <p className="text-xs text-white/20 mb-3 font-mono">{product.sku}</p>

          <div className="flex items-end justify-between mb-3">
            <span className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>${product.price?.toFixed(2) || "0.00"}</span>
            {product.stock_quantity > 0 && (
              <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full font-medium border border-emerald-500/10">{product.stock_quantity} in stock</span>
            )}
          </div>

          <button onClick={() => variantCount > 1 ? openProductModal(product) : addToCart(product)}
            className={`w-full py-2.5 rounded-full text-sm font-semibold transition-all active:scale-[0.98] ${
              variantCount > 1
                ? "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
                : "bg-white text-black hover:bg-white/90"
            }`} data-testid="add-to-cart-btn">
            {variantCount > 1 ? "Select Flavor" : "Add to Cart"}
          </button>
        </div>
      </div>
    );
  };

  // ============================================
  // PRODUCT DETAIL MODAL - Dark style
  // ============================================
  const ProductDetailModal = () => {
    if (!productModal) return null;
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setProductModal(null)}>
        <div className="bg-zinc-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-white/10" onClick={e => e.stopPropagation()} data-testid="product-detail-modal">
          <div className="p-6 border-b border-white/5 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{productModal.name}</h2>
            <button onClick={() => setProductModal(null)} className="p-2 hover:bg-white/5 rounded-lg transition-colors text-white/40 hover:text-white" data-testid="close-modal-btn"><X size={22} /></button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="aspect-square bg-zinc-800/50 rounded-xl overflow-hidden p-8">
                {productModal.image_url ? (
                  <img src={productModal.image_url} alt={productModal.name} className="w-full h-full object-contain" />
                ) : <div className="w-full h-full flex items-center justify-center text-white/10"><Package size={80} /></div>}
              </div>
              <div className="flex flex-col justify-center">
                <span className="text-xs font-medium text-white/30 uppercase tracking-widest">{productModal.category}</span>
                <p className="text-sm text-white/30 mt-2 font-mono">SKU: {productModal.sku}</p>
                {productModal.product_url && (
                  <a href={productModal.product_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:text-blue-300 mt-1 inline-flex items-center gap-1">
                    View on supplier <ArrowRight size={12} />
                  </a>
                )}
                <div className="mt-6 flex items-baseline gap-3">
                  <span className="text-4xl font-extrabold text-white" style={{ fontFamily: 'Manrope' }}>${productModal.price?.toFixed(2) || "0.00"}</span>
                  {productModal.stock_quantity > 0 && <span className="text-sm text-emerald-400 font-medium">{productModal.stock_quantity} in stock</span>}
                </div>
                {productVariants.length === 0 && (
                  <button onClick={() => { addToCart(productModal); setProductModal(null); }}
                    className="mt-6 px-8 py-3 bg-white text-black rounded-full font-bold hover:bg-white/90 transition-all hover:scale-105 active:scale-95">
                    Add to Cart
                  </button>
                )}
              </div>
            </div>

            {productVariants.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                  Available Flavors <span className="text-white/30 font-normal text-base">({productVariants.length})</span>
                </h3>
                <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="variant-table">
                  <table className="w-full">
                    <thead><tr className="bg-white/5">
                      <th className="text-left p-3 text-xs font-semibold text-white/40 uppercase tracking-wider">Flavor</th>
                      <th className="text-left p-3 text-xs font-semibold text-white/40 uppercase tracking-wider">SKU</th>
                      <th className="text-left p-3 text-xs font-semibold text-white/40 uppercase tracking-wider">Price</th>
                      <th className="text-left p-3 text-xs font-semibold text-white/40 uppercase tracking-wider">Stock</th>
                      <th className="text-right p-3 text-xs font-semibold text-white/40 uppercase tracking-wider">Action</th>
                    </tr></thead>
                    <tbody>
                      {productVariants.map((v, i) => (
                        <tr key={v.sku} className={`border-t border-white/5 hover:bg-white/5 transition-colors ${i % 2 === 0 ? "" : "bg-white/[0.02]"}`}>
                          <td className="p-3 font-medium text-sm text-white">{v.name}</td>
                          <td className="p-3 text-xs text-white/30 font-mono">{v.sku}</td>
                          <td className="p-3 text-sm font-bold text-white">${v.price?.toFixed(2)}</td>
                          <td className="p-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${v.stock_quantity > 0 ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/10" : "bg-red-500/10 text-red-400 border border-red-500/10"}`}>{v.stock_quantity > 0 ? `${v.stock_quantity}` : "Out"}</span></td>
                          <td className="p-3 text-right">
                            <button onClick={() => { addToCart({ ...v, image_url: productModal.image_url, category: productModal.category }); setProductModal(null); }}
                              disabled={v.stock_quantity === 0}
                              className="px-4 py-1.5 bg-white text-black rounded-full text-xs font-semibold hover:bg-white/90 transition-all disabled:opacity-20 disabled:cursor-not-allowed active:scale-95"
                              data-testid={`add-variant-${v.sku}`}>
                              Add to Cart
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // AUTH PAGE - Dark
  // ============================================
  const AuthPage = () => (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 bg-black">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-black font-extrabold text-2xl" style={{ fontFamily: 'Manrope' }}>IB</span>
          </div>
          <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
            {authTab === "login" ? "Welcome back" : "Create account"}
          </h2>
          <p className="text-white/40 mt-2">
            {authTab === "login" ? "Sign in to access wholesale pricing" : "Join our wholesale community"}
          </p>
        </div>

        <div className="flex bg-white/5 rounded-full p-1 mb-6 border border-white/5">
          <button onClick={() => setAuthTab("login")} className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${authTab === "login" ? "bg-white text-black" : "text-white/50 hover:text-white"}`} data-testid="auth-login-tab">Sign In</button>
          <button onClick={() => setAuthTab("register")} className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${authTab === "register" ? "bg-white text-black" : "text-white/50 hover:text-white"}`} data-testid="auth-register-tab">Register</button>
        </div>

        {authTab === "login" ? (
          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-white/60 mb-1.5">Email</label>
              <input type="text" value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20 transition-all"
                placeholder="you@company.com" required data-testid="login-email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/60 mb-1.5">Password</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20 transition-all pr-10"
                  placeholder="Enter password" required data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full py-3 bg-white text-black rounded-full font-semibold hover:bg-white/90 transition-all active:scale-[0.98] disabled:opacity-50">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-3" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1">First Name *</label>
                <input type="text" value={registerForm.firstName} onChange={e => setRegisterForm({...registerForm, firstName: e.target.value})}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20" required data-testid="register-firstname" />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1">Last Name *</label>
                <input type="text" value={registerForm.lastName} onChange={e => setRegisterForm({...registerForm, lastName: e.target.value})}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20" required data-testid="register-lastname" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1">Business Email *</label>
              <input type="email" value={registerForm.email} onChange={e => setRegisterForm({...registerForm, email: e.target.value})}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20" required data-testid="register-email" />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1">Password *</label>
              <input type="password" value={registerForm.password} onChange={e => setRegisterForm({...registerForm, password: e.target.value})}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20" required data-testid="register-password" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1">Phone</label>
                <input type="text" value={registerForm.phone} onChange={e => setRegisterForm({...registerForm, phone: e.target.value})}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1">Business Name</label>
                <input type="text" value={registerForm.businessName} onChange={e => setRegisterForm({...registerForm, businessName: e.target.value})}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1">Business Address</label>
              <input type="text" value={registerForm.businessAddress} onChange={e => setRegisterForm({...registerForm, businessAddress: e.target.value})}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input type="text" placeholder="City" value={registerForm.city} onChange={e => setRegisterForm({...registerForm, city: e.target.value})}
                className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20" />
              <input type="text" placeholder="State" value={registerForm.state} onChange={e => setRegisterForm({...registerForm, state: e.target.value})}
                className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20" />
              <input type="text" placeholder="Zip" value={registerForm.zipCode} onChange={e => setRegisterForm({...registerForm, zipCode: e.target.value})}
                className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20" />
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full py-3 bg-white text-black rounded-full font-semibold hover:bg-white/90 transition-all active:scale-[0.98] disabled:opacity-50">
              {loading ? "Creating..." : "Create Account"}
            </button>
          </form>
        )}

        <p className="text-center text-xs text-white/20 mt-6">
          Need help? Contact us at <span className="font-medium text-white/40">{s.phone || "+1 (630)422-1915"}</span>
        </p>
      </div>
    </div>
  );

  // ============================================
  // SHOP PAGE - Dark
  // ============================================
  const ShopPage = () => (
    <div className="bg-black min-h-screen pt-24 pb-16">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-10">
          <div>
            <p className="text-white/30 text-xs tracking-[0.3em] uppercase mb-2">Browse</p>
            <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>
              {selectedCategory === "all" ? "All Products" : selectedCategory}
            </h1>
            <p className="text-white/30 mt-2">{filteredProducts.length} products available</p>
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSelectedCategory("all")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${selectedCategory === "all" ? "bg-white text-black" : "bg-white/5 text-white/50 border border-white/10 hover:bg-white/10"}`}>
              All
            </button>
            {categories.map(cat => (
              <button key={cat.name} onClick={() => setSelectedCategory(cat.name)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${selectedCategory === cat.name ? "bg-white text-black" : "bg-white/5 text-white/50 border border-white/10 hover:bg-white/10"}`}>
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile search */}
        <div className="flex md:hidden w-full mb-6">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={18} />
            <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-white/20" />
          </div>
        </div>

        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {filteredProducts.map(p => <ProductCard key={p.sku} product={p} />)}
          </div>
        ) : (
          <div className="text-center py-20">
            <Box size={64} className="mx-auto text-white/10 mb-4" />
            <h3 className="text-xl font-semibold text-white/30">No products found</h3>
            <p className="text-white/20 mt-2">Try a different search or category</p>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // CART PAGE - Dark
  // ============================================
  const CartPage = () => (
    <div className="bg-black min-h-screen pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <h1 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Manrope' }}>Shopping Cart</h1>
        {cart.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-3">
              {cart.map(item => (
                <div key={item.sku} className="flex gap-4 p-4 bg-zinc-900/60 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
                  <div className="w-20 h-20 bg-zinc-800/50 rounded-lg flex-shrink-0 overflow-hidden p-2">
                    {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-contain" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-white text-sm truncate">{item.name}</h4>
                    <p className="text-xs text-white/20 mt-0.5 font-mono">{item.sku}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center border border-white/10 rounded-full overflow-hidden">
                        <button onClick={() => updateCartQuantity(item.sku, item.quantity - 1)} className="px-3 py-1.5 hover:bg-white/5 text-white/50"><Minus size={14} /></button>
                        <span className="px-3 py-1.5 text-sm font-medium text-white min-w-[40px] text-center">{item.quantity}</span>
                        <button onClick={() => updateCartQuantity(item.sku, item.quantity + 1)} className="px-3 py-1.5 hover:bg-white/5 text-white/50"><Plus size={14} /></button>
                      </div>
                      <span className="text-lg font-bold text-white">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                  <button onClick={() => removeFromCart(item.sku)} className="text-white/20 hover:text-red-400 transition-colors self-start p-1"><X size={18} /></button>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-zinc-900/60 rounded-2xl border border-white/5 p-6 sticky top-24">
                <h3 className="font-bold text-lg text-white mb-4" style={{ fontFamily: 'Manrope' }}>Order Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-white/40"><span>Subtotal ({cartCount} items)</span><span className="text-white font-medium">${cartTotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-white/40"><span>Shipping</span><span className="text-emerald-400 font-medium">{cartTotal >= 500 ? "Free" : "Calculated at checkout"}</span></div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/5 flex justify-between font-bold text-xl text-white">
                  <span>Total</span><span>${cartTotal.toFixed(2)}</span>
                </div>
                <button data-testid="checkout-btn"
                  onClick={async () => {
                    if (!isLoggedIn) { toast.error("Please sign in first"); setCurrentPage("home"); return; }
                    try {
                      const res = await axios.post(`${API}/storefront/orders`, {
                        customer: { name: user?.name || "Guest", email: user?.email || "", phone: user?.phone || "" },
                        items: cart.map(i => ({ sku: i.sku, name: i.name, price: i.price, quantity: i.quantity, image_url: i.image_url })),
                        total: cartTotal,
                      });
                      setCart([]); const o = res.data.order;
                      setOrders(prev => [{ ...o, id: o.order_id, date: o.created_at }, ...prev]);
                      toast.success("Order placed!"); setCurrentPage("account"); setAccountTab("orders");
                    } catch { toast.error("Failed to place order"); }
                  }}
                  className="w-full mt-6 py-3 bg-white text-black rounded-full font-bold hover:bg-white/90 transition-all hover:scale-[1.02] active:scale-[0.98]">
                  Place Order
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-20">
            <ShoppingCart size={64} className="mx-auto text-white/10 mb-4" />
            <p className="text-white/40 mb-6">Your cart is empty</p>
            <button onClick={() => setCurrentPage("shop")} className="px-6 py-2.5 bg-white text-black rounded-full font-semibold hover:bg-white/90 transition-all">Browse Products</button>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // ACCOUNT PAGE - Dark
  // ============================================
  const AccountPage = () => (
    <div className="bg-black min-h-screen pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <div className="bg-zinc-900/60 rounded-2xl border border-white/5 p-4">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/5">
                <div className="w-10 h-10 bg-white/10 text-white rounded-full flex items-center justify-center font-bold border border-white/10">{user?.name?.charAt(0)?.toUpperCase() || "U"}</div>
                <div>
                  <p className="font-semibold text-white text-sm">{user?.name}</p>
                  <p className="text-xs text-white/30">{user?.email}</p>
                </div>
              </div>
              <nav className="space-y-1">
                {[{ id: "dashboard", label: "Dashboard" }, { id: "orders", label: "Orders" }, { id: "wishlist", label: "Wishlist" }, { id: "addresses", label: "Addresses" }, { id: "password", label: "Settings" }].map(item => (
                  <button key={item.id} onClick={() => setAccountTab(item.id)}
                    className={`w-full text-left py-2 px-3 rounded-lg text-sm font-medium transition-all ${accountTab === item.id ? "bg-white text-black" : "text-white/50 hover:bg-white/5 hover:text-white"}`}>
                    {item.label}
                  </button>
                ))}
                <button onClick={handleLogout} className="w-full text-left py-2 px-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all flex items-center gap-2" data-testid="logout-btn"><LogOut size={14} />Logout</button>
              </nav>
            </div>
          </div>

          <div className="md:col-span-3">
            {accountTab === "dashboard" && (
              <div className="bg-zinc-900/60 rounded-2xl border border-white/5 p-6">
                <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>Dashboard</h2>
                <p className="text-white/40">Welcome back, <strong className="text-white">{user?.name}</strong>. Manage your orders and account settings.</p>
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/5"><p className="text-2xl font-bold text-white">{orders.length}</p><p className="text-xs text-white/30">Total Orders</p></div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/5"><p className="text-2xl font-bold text-white">{wishlist.length}</p><p className="text-xs text-white/30">Wishlist Items</p></div>
                </div>
              </div>
            )}

            {accountTab === "orders" && (
              <div>
                <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Orders</h2>
                {orders.length > 0 ? (
                  <div className="space-y-4">
                    {orders.map(order => (
                      <div key={order.id} className="bg-zinc-900/60 rounded-2xl border border-white/5 p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-bold text-white text-sm font-mono">{order.id}</p>
                            <p className="text-xs text-white/30 mt-0.5">{order.date ? new Date(order.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-"}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            order.status === "delivered" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/10" :
                            order.status === "shipped" ? "bg-blue-500/10 text-blue-400 border border-blue-500/10" :
                            order.status === "processing" ? "bg-amber-500/10 text-amber-400 border border-amber-500/10" :
                            "bg-white/5 text-white/50 border border-white/5"
                          }`}>{order.status}</span>
                        </div>
                        <div className="border-t border-white/5 pt-3 space-y-2">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-zinc-800/50 rounded-lg overflow-hidden flex-shrink-0 p-1">
                                {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-contain" />}
                              </div>
                              <div className="flex-1 min-w-0"><p className="text-sm text-white/70 truncate">{item.name}</p></div>
                              <p className="text-sm text-white/40">{item.quantity} x ${item.price?.toFixed(2)}</p>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-white/5 pt-3 mt-3 flex justify-between"><span className="font-medium text-white/40 text-sm">Total</span><span className="font-bold text-lg text-white">${order.total?.toFixed(2)}</span></div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-white/30 bg-zinc-900/60 rounded-2xl border border-white/5 p-8 text-center">No orders yet. Start shopping!</p>}
              </div>
            )}

            {accountTab === "wishlist" && (
              <div>
                <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Wishlist</h2>
                {wishlist.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {wishlist.map(item => (
                      <div key={item.sku} className="bg-zinc-900/60 rounded-2xl border border-white/5 p-4 hover:border-white/15 transition-all">
                        <div className="aspect-square bg-zinc-800/30 rounded-xl mb-3 p-3 overflow-hidden">
                          {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-contain" />}
                        </div>
                        <p className="text-sm font-medium text-white line-clamp-2">{item.name}</p>
                        <p className="font-bold text-white mt-1">${item.price?.toFixed(2)}</p>
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => addToCart(item)} className="flex-1 py-1.5 bg-white text-black rounded-full text-xs font-semibold hover:bg-white/90">Add to Cart</button>
                          <button onClick={() => toggleWishlist(item)} className="px-3 py-1.5 border border-white/10 rounded-full text-xs text-red-400 hover:bg-red-500/10">Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-white/30 bg-zinc-900/60 rounded-2xl border border-white/5 p-8 text-center">Your wishlist is empty.</p>}
              </div>
            )}

            {accountTab === "addresses" && (
              <div className="bg-zinc-900/60 rounded-2xl border border-white/5 p-6">
                <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Addresses</h2>
                <p className="text-white/30">No saved addresses yet.</p>
              </div>
            )}

            {accountTab === "password" && (
              <div className="bg-zinc-900/60 rounded-2xl border border-white/5 p-6">
                <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Account Settings</h2>
                <form className="max-w-md space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1">Display Name</label>
                    <input type="text" defaultValue={user?.name} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-white/20" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-1">Email</label>
                    <input type="email" defaultValue={user?.email} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-white/20" />
                  </div>
                  <button type="submit" className="px-6 py-2.5 bg-white text-black rounded-full font-semibold hover:bg-white/90 transition-all">Save Changes</button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================
  // BRAND SECTION (GeekBar about-style)
  // ============================================
  const BrandSection = () => (
    <div className="bg-black py-20 border-t border-white/5" data-testid="brand-section">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6">
          <span className="text-black font-extrabold text-2xl" style={{ fontFamily: 'Manrope' }}>IB</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight" style={{ fontFamily: 'Manrope' }}>IMPORTBABA</h2>
        <p className="text-white/30 text-base leading-relaxed max-w-xl mx-auto">
          Your trusted wholesale partner, offering premium products at competitive prices.
          We source directly from manufacturers to deliver the best quality products
          for your retail business.
        </p>
        <button onClick={() => setCurrentPage("shop")}
          className="mt-8 px-8 py-3 border border-white/20 text-white rounded-full text-sm font-medium hover:bg-white/5 transition-all tracking-wide">
          VIEW MORE
        </button>
      </div>
    </div>
  );

  // ============================================
  // FOOTER - Dark
  // ============================================
  const bh = s.business_hours || {};
  const Footer = () => (
    <footer className="bg-zinc-950 text-white/40 border-t border-white/5">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center">
                <span className="text-black font-extrabold text-sm" style={{ fontFamily: 'Manrope' }}>IB</span>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope' }}>ImportBaba</h3>
                <p className="text-[9px] text-white/20 tracking-[0.25em] uppercase">Wholesale Hub</p>
              </div>
            </div>
            <p className="flex items-start gap-2 text-sm text-white/30 mt-3">
              <MapPin size={16} className="flex-shrink-0 mt-0.5" />
              {s.address || "1049 Industrial Dr, Bensenville, IL 60106"}
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm tracking-wide">Contact</h4>
            <p className="flex items-center gap-2 text-sm text-white/30 mb-2"><Phone size={14} />{s.phone || "+1 (630)422-1915"}</p>
            <p className="flex items-center gap-2 text-sm text-white/30 mb-4"><Mail size={14} />{s.email || "info@importbaba.com"}</p>
            <p className="text-xs text-white/20">Mon-Fri: {bh.mon_fri || "10am - 7pm CST"}</p>
            <p className="text-xs text-white/20">Sat: {bh.sat || "10am - 6pm CST"}</p>
            <p className="text-xs text-white/20">Sun: {bh.sun || "Closed"}</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm tracking-wide">Company</h4>
            <ul className="space-y-2 text-sm text-white/30">
              <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
              <li><a href="#" className="hover:text-white transition-colors">FAQ's</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Return Policy</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm tracking-wide">Quick Links</h4>
            <ul className="space-y-2 text-sm text-white/30">
              <li><button onClick={() => setCurrentPage("shop")} className="hover:text-white transition-colors">Shop</button></li>
              <li><button onClick={() => setCurrentPage("cart")} className="hover:text-white transition-colors">Cart</button></li>
              <li><button onClick={() => setCurrentPage("account")} className="hover:text-white transition-colors">My Account</button></li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-white/5 py-4 text-center text-xs text-white/20">
        &copy; {new Date().getFullYear()} ImportBaba. All rights reserved.
      </div>
    </footer>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Toaster position="top-right" richColors closeButton theme="dark" />
      <Header />

      <main className="flex-1">
        {!isLoggedIn && currentPage === "home" && (
          <>
            <HeroBanner />
            <Marquee />
            <TrustSection />
            <AuthPage />
            <BrandSection />
          </>
        )}
        {isLoggedIn && currentPage === "home" && (
          <>
            <HeroBanner />
            <Marquee />
            <TrustSection />
            <ProductShowcase />
            <BrandSection />
          </>
        )}
        {currentPage === "shop" && <ShopPage />}
        {currentPage === "cart" && <CartPage />}
        {currentPage === "account" && <AccountPage />}
      </main>

      <Footer />
      <ProductDetailModal />
      {showMiniCart && <div className="fixed inset-0 z-40" onClick={() => setShowMiniCart(false)} />}
    </div>
  );
};

export default ImportBaba;
